import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

import type { MaintenanceStatus, MaintenanceWindow } from "@app-types/maintenance.type";
import { isValidTimeZone } from "./date.util";

// Idempotent; this module must not rely on date.util having extended these first.
dayjs.extend(utc);
dayjs.extend(timezone);

const START_KEYS = [
  "startsAt",
  "startAt",
  "start",
  "startTime",
  "starts_at",
  "start_time",
  "from",
  "begin",
  "beginsAt",
];
const END_KEYS = [
  "endsAt",
  "endAt",
  "end",
  "endTime",
  "ends_at",
  "end_time",
  "to",
  "finish",
  "finishesAt",
];
const ZONE_KEYS = [
  "organizationTimeZone",
  "organization_time_zone",
  "orgTimeZone",
  "timeZone",
  "timezone",
  "tz",
  "zone",
];
const WRAPPER_KEYS = ["maintenance", "data", "schedule", "window", "result"];
const ACTIVE_KEYS = ["is_active", "isActive", "active", "enabled"];
const MESSAGE_KEYS = ["message"];

function pickZone(source: Record<string, unknown>): string | undefined {
  for (const key of ZONE_KEYS) {
    const value = source[key];
    if (typeof value === "string" && value.trim() !== "") return value;
  }
  return undefined;
}

function pickInstant(source: Record<string, unknown>, keys: string[]): string | number | undefined {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string" && value.trim() !== "") return value;
  }
  return undefined;
}

// Trailing `Z` or a `±HH:MM` / `±HHMM` offset marks a string as an absolute instant.
const HAS_OFFSET = /(?:Z|[+-]\d{2}:?\d{2})$/;

/**
 * Converts a raw start/end value to an absolute ISO instant.
 *
 * - Numbers are treated as epoch milliseconds.
 * - Strings carrying an offset / `Z` are absolute and parsed as-is.
 * - Offset-less strings are wall-clock times **in the organization's timezone** (NOT the
 *   client's local zone), so a naive "2026-01-21 02:00" maps to the correct UTC instant.
 *
 * Wrapped in try/catch because `dayjs.tz` *throws* on unparseable input instead of returning
 * an invalid date — without this, a junk value would take the caller down instead of degrading.
 */
function toInstant(value: string | number, timeZone: string): string | null {
  try {
    if (typeof value === "number") {
      const day = dayjs(value);
      return day.isValid() ? day.toISOString() : null;
    }
    const trimmed = value.trim();
    const day = HAS_OFFSET.test(trimmed) ? dayjs(trimmed) : dayjs.tz(trimmed, timeZone);
    return day.isValid() ? day.toISOString() : null;
  } catch {
    return null;
  }
}

/**
 * Normalizes a `503` body / schedule payload into a {@link MaintenanceWindow}, tolerating the
 * not-yet-finalized backend contract (MER-8343): several field names, numeric or string
 * instants, and one level of nesting. `start`/`end` always come from the same source object.
 * Returns `null` when no valid start/end pair is found.
 */
export function parseMaintenanceWindow(raw: unknown): MaintenanceWindow | null {
  if (!raw || typeof raw !== "object") return null;
  const source = raw as Record<string, unknown>;

  // Zone may live at the top level even when the window is nested.
  const fallbackZone = pickZone(source);

  const candidates: Record<string, unknown>[] = [source];
  for (const wrapperKey of WRAPPER_KEYS) {
    const nested = source[wrapperKey];
    if (nested && typeof nested === "object") candidates.push(nested as Record<string, unknown>);
  }

  for (const candidate of candidates) {
    const start = pickInstant(candidate, START_KEYS);
    const end = pickInstant(candidate, END_KEYS);
    if (start === undefined || end === undefined) continue;

    const rawZone = pickZone(candidate) ?? fallbackZone;
    // Fall back to UTC so times still render even when the zone is missing/invalid.
    const timeZone = rawZone && isValidTimeZone(rawZone) ? rawZone : "UTC";

    const startsAt = toInstant(start, timeZone);
    const endsAt = toInstant(end, timeZone);
    if (!startsAt || !endsAt) continue;

    return { startsAt, endsAt, organizationTimeZone: timeZone };
  }

  return null;
}

function pickBoolean(source: Record<string, unknown>, keys: string[]): boolean {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === "boolean") return value;
    if (typeof value === "string") return ["true", "1", "yes"].includes(value.trim().toLowerCase());
  }
  return false;
}

function pickMessage(source: Record<string, unknown>, keys: string[]): string {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === "string" && value.trim() !== "") return value.trim();
  }
  return "";
}

/**
 * Normalizes the tools service payload into a {@link MaintenanceStatus}. A payload without the
 * flag — an error body like `{ detail: "Not Found" }` — returns `null` rather than reading as
 * "maintenance is off".
 */
export function parseMaintenanceStatus(raw: unknown): MaintenanceStatus | null {
  if (!raw || typeof raw !== "object") return null;
  const source = raw as Record<string, unknown>;
  if (!ACTIVE_KEYS.some((key) => key in source)) return null;

  return {
    isActive: pickBoolean(source, ACTIVE_KEYS),
    window: parseMaintenanceWindow(source),
    message: pickMessage(source, MESSAGE_KEYS),
  };
}

/** Short timezone label as seen at the window start, e.g. "EST" or "GMT+3". */
export function getTimeZoneAbbreviation(timeZone: string, atISO: string): string {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      timeZoneName: "short",
    }).formatToParts(new Date(atISO));
    return parts.find((part) => part.type === "timeZoneName")?.value ?? "";
  } catch {
    return "";
  }
}

/** "2:00 AM – 5:00 AM" — the window rendered in the given timezone. */
export function formatWindowRange(window: MaintenanceWindow, timeZone: string): string {
  const start = dayjs.utc(window.startsAt).tz(timeZone).format("h:mm A");
  const end = dayjs.utc(window.endsAt).tz(timeZone).format("h:mm A");
  return `${start} – ${end}`;
}

const TIME_MASK = "h:mm A";
const DATE_MASK = "MM/DD/YYYY";
const DATE_TIME_MASK = `${DATE_MASK}, ${TIME_MASK}`;

/**
 * The window's pieces, carrying only the dates the reader needs:
 *
 * - spans two days → both endpoints dated
 * - one day        → the start dated, the end bare
 * - one day, today → neither dated, the times alone are unambiguous
 *
 * `date`/`start`/`end` are pre-joined for the alert modal, which hoists a shared day next to its
 * label. `startDate`/`startTime`/`endDate`/`endTime` keep them apart for the maintenance screen,
 * which sets the times in bold against muted dates.
 */
export function formatWindowParts(
  window: MaintenanceWindow,
  timeZone: string,
  now: Date = new Date()
): {
  date: string | null;
  start: string;
  end: string;
  startDate: string | null;
  startTime: string;
  endDate: string | null;
  endTime: string;
} {
  const start = dayjs.utc(window.startsAt).tz(timeZone);
  const end = dayjs.utc(window.endsAt).tz(timeZone);
  const isOneDay = start.isSame(end, "day");
  const isToday = start.isSame(dayjs(now).tz(timeZone), "day");
  const startDate = isOneDay && isToday ? null : start.format(DATE_MASK);
  const endDate = isOneDay ? null : end.format(DATE_MASK);

  return {
    // Combined chunks for the alert modal, which hoists a shared day next to its label.
    date: isOneDay ? startDate : null,
    start: isOneDay ? start.format(TIME_MASK) : start.format(DATE_TIME_MASK),
    end: isOneDay ? end.format(TIME_MASK) : end.format(DATE_TIME_MASK),
    // Split out for the maintenance screen, which styles dates and times differently.
    startDate,
    startTime: start.format(TIME_MASK),
    endDate,
    endTime: end.format(TIME_MASK),
  };
}

/** Stable identity for a window, used to only show the alert once per window per device. */
export function getMaintenanceWindowKey(window: MaintenanceWindow): string {
  return window.startsAt;
}

/**
 * True when "now", in the given timezone, falls on the calendar day immediately before
 * the maintenance start — i.e. the alert should be shown on first login that day.
 */
export function isMaintenanceAlertDay(
  window: MaintenanceWindow,
  timeZone: string,
  now: Date = new Date()
): boolean {
  const start = dayjs.utc(window.startsAt);
  if (!start.isValid()) return false;
  const dayBefore = start.tz(timeZone).subtract(1, "day");
  return dayjs(now).tz(timeZone).isSame(dayBefore, "day");
}
